import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Typography } from '@mui/material';
import moment from 'moment';
import { renderStatus } from 'components/v2/cell-renderers/status';
import { tableV2StackedCellContentSx } from '../../../../components/v2/tableV2Styles';
import {
  getArea,
  destacarVencimento,
  obterTextoVencimento,
  formatDate,
  shouldShowExpirationChip,
} from '../utils';

const LabeledLine = ({ label, value, variant = 'body2' }) => {
  if (value == null || value === '') return null;
  return (
    <Typography
      variant={variant}
      component="div"
      sx={{ lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word' }}
    >
      {label ? (
        <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
          {label}
        </Box>
      ) : null}
      {value}
    </Typography>
  );
};

LabeledLine.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.string,
};

LabeledLine.defaultProps = {
  label: '',
  value: '',
  variant: 'body2',
};

const priorityChipSx = {
  fontWeight: 600,
  color: '#bd8c00',
  bgcolor: 'transparent',
  border: '1px solid #bd8c00',
};

const urgencyChipSx = (theme) => ({
  fontWeight: 600,
  color: theme.palette.error.main,
  bgcolor: 'transparent',
  border: `1px solid ${theme.palette.error.main}`,
});

const adereChipSx = (theme) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  bgcolor: 'transparent',
  border: `1px solid ${theme.palette.primary.main}`,
});

export const EquipamentoNsirCell = React.memo(({ data }) => {
  const hasPriority = data.prioridade === 'SIM';
  const hasUrgency = !!data.nsmpUrgencia;
  const showChips = hasPriority || hasUrgency || data.possuiAdere;

  return (
    <Box sx={tableV2StackedCellContentSx}>
      <LabeledLine label="NSIR:" value={data.codigo} />
      {data.equipamento && <LabeledLine label="Equipamento:" value={data.equipamento} />}
      {showChips && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {hasPriority && (
            <Chip label="Prioridade" size="small" sx={priorityChipSx} aria-label="Prioridade alta" />
          )}
          {hasUrgency && (
            <Chip
              label="Urgente"
              size="small"
              sx={(theme) => urgencyChipSx(theme)}
              aria-label="NSMP urgente"
            />
          )}
          {data.possuiAdere && (
            <Chip
              label={`Adere ${data.adereMonth}`}
              size="small"
              sx={(theme) => adereChipSx(theme)}
              aria-label={data.adereMonth}
            />
          )}
        </Box>
      )}
    </Box>
  );
});

EquipamentoNsirCell.propTypes = {
  data: PropTypes.shape({
    codigo: PropTypes.string,
    equipamento: PropTypes.string,
    prioridade: PropTypes.string,
    nsmpUrgencia: PropTypes.bool,
    possuiAdere: PropTypes.bool,
    adereMonth: PropTypes.string,
  }).isRequired,
};

export const CtPlanExecCell = React.memo(({ data }) => (
  <Box sx={tableV2StackedCellContentSx}>
    <LabeledLine label="Plan:" value={data.ctPlan} />
    <LabeledLine label="Exec:" value={data.ctExec} />
  </Box>
));

CtPlanExecCell.propTypes = {
  data: PropTypes.shape({
    ctPlan: PropTypes.string,
    ctExec: PropTypes.string,
  }).isRequired,
};

export const MalhaRegionalAlimentadorCell = React.memo(({ data }) => (
  <Box sx={tableV2StackedCellContentSx}>
    <LabeledLine label="Malha/Regional:" value={data.malhaCodigo} />
    <LabeledLine label="Alimentador:" value={data.alimentadorCodigo} />
  </Box>
));

MalhaRegionalAlimentadorCell.propTypes = {
  data: PropTypes.shape({
    malhaCodigo: PropTypes.string,
    alimentadorCodigo: PropTypes.string,
  }).isRequired,
};

export const TipoServicoCell = React.memo(({ data }) => (
  <Box sx={tableV2StackedCellContentSx}>
    {data.tipoServicoName && (
      <Typography variant="body2" sx={{ lineHeight: 1.2, whiteSpace: 'normal' }}>
        {data.tipoServicoName}
      </Typography>
    )}
    {data.subtipoName && (
      <Typography variant="caption" component="div" sx={{ lineHeight: 1.2, color: 'text.secondary' }}>
        ({data.subtipoName})
      </Typography>
    )}
  </Box>
));

TipoServicoCell.propTypes = {
  data: PropTypes.shape({
    tipoServicoName: PropTypes.string,
    subtipoName: PropTypes.string,
  }).isRequired,
};

export const DatasCell = React.memo(({ data }) => {
  const dataVencimento = moment(data.vencimento).endOf('day');
  const showExpirationChip = shouldShowExpirationChip(data);
  const chipStyle = destacarVencimento(dataVencimento, moment());

  return (
    <Box sx={tableV2StackedCellContentSx}>
      <LabeledLine label="Vencimento:" value={formatDate(dataVencimento)} />
      {showExpirationChip && (
        <Chip
          variant="outlined"
          style={chipStyle}
          size="small"
          label={obterTextoVencimento(dataVencimento, moment())}
          sx={{ mt: 0.5, fontWeight: 600, alignSelf: 'flex-start' }}
        />
      )}
      {data.dataConclusaoValue && (
        <LabeledLine label="Exec. GDIS:" value={data.dataConclusaoValue} variant="caption" />
      )}
    </Box>
  );
});

DatasCell.propTypes = {
  data: PropTypes.shape({
    vencimento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dataConclusao: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dataConclusaoValue: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

export const AreaCell = React.memo(({ data }) => (
  <Box sx={tableV2StackedCellContentSx}>
    <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
      {getArea(data.area)}
    </Typography>
  </Box>
));

AreaCell.propTypes = {
  data: PropTypes.shape({
    area: PropTypes.string,
  }).isRequired,
};

export const TextCell = React.memo(({ value }) => (
  <Box sx={tableV2StackedCellContentSx}>
    <Typography variant="body2" sx={{ lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
));

TextCell.propTypes = {
  value: PropTypes.string,
};

TextCell.defaultProps = {
  value: '',
};

export const StatusCell = React.memo(({ data }) => (
  <Box sx={tableV2StackedCellContentSx}>
    <Box sx={{ width: 'fit-content', maxWidth: '100%', alignSelf: 'flex-start' }}>
      {renderStatus({ value: data.status, label: data.statusName })}
    </Box>
  </Box>
));

StatusCell.propTypes = {
  data: PropTypes.shape({
    status: PropTypes.string,
    statusName: PropTypes.string,
  }).isRequired,
};
